/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_RATINGS_REVIEWS */

import { useEffect, useState } from 'react';
import { createLogger } from '@/lib/logger';

const logger = createLogger();

/** Minimal shape of the global BV object once bv.js has loaded. */
interface BvGlobal {
    /** Re-scan the DOM and (re)render widgets. Present on the hosted display API. */
    reveal?: () => void;
}

declare global {
    interface Window {
        BV?: BvGlobal;
        $BV?: unknown;
        /** BV invokes this once the loader is ready; used for pixel/event registration. */
        bvCallback?: (bv: BvGlobal) => void;
    }
}

export type BvScriptStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Lazily inject the Bazaarvoice `bv.js` "Scout" loader.
 *
 * Appends a single `<script async>` to `document.body`, guarded against
 * duplicate injection (skips if `window.BV`/`window.$BV` already exists or a
 * script with the same src is present). The script is left in place for the
 * session — no cleanup on unmount, so navigating between PDPs doesn't
 * re-download the loader.
 *
 * Pass `null`/empty to disable (e.g. before the widget is mounted or when
 * config is incomplete) — nothing is injected and status stays `idle`. This is
 * the mechanism that keeps bv.js off the initial page load: the consuming
 * component only supplies a URL once the reviews section is in view.
 */
export function useBvScript(loaderUrl: string | null | undefined): BvScriptStatus {
    const [status, setStatus] = useState<BvScriptStatus>('idle');

    useEffect(() => {
        if (!loaderUrl || typeof window === 'undefined') return;

        // Already loaded in this session (e.g. from a prior PDP visit).
        if (window.BV || window.$BV) {
            setStatus('ready');
            return;
        }

        // Exact match — a substring test would false-positive on any script whose
        // src merely contains this URL and wrongly report the loader as ready.
        const existing = Array.from(document.querySelectorAll('script')).find((s) => s.src === loaderUrl);
        if (existing) {
            setStatus('ready');
            return;
        }

        setStatus('loading');
        const script = document.createElement('script');
        // loaderUrl is an absolute https URL (see getBvLoaderUrl) — BV serves the
        // loader over https only and the CSP allows only the https origin.
        script.src = loaderUrl;
        script.async = true;
        script.onload = () => setStatus('ready');
        script.onerror = () => {
            logger.error('Failed to load Bazaarvoice bv.js loader', { loaderUrl });
            setStatus('error');
        };
        document.body.appendChild(script);
        // Left in place for the session; no cleanup on unmount.
    }, [loaderUrl]);

    return status;
}
